import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';
import { environment } from '../environments/environment';
import { AuthService } from './auth.service';
import { normalizeSeriesDetail } from './catalog-response.util';

export interface PagedResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface MovieSummary {
  id: string;
  titleEn: string;
  titleVi: string;
  genre: string;
  releaseYear: number;
  durationMinutes?: number | null;
  posterUrl?: string | null;
  hasMedia?: boolean;
  isVip?: boolean;
  isFeatured?: boolean;
  topCast?: string | null;
  topDirectors?: string | null;
}

export interface MovieCredit {
  name: string;
  role: 'director' | 'actor';
  characterName?: string | null;
  profileImageUrl?: string | null;
  sortOrder?: number;
  externalPersonId?: string | null;
  personId?: string | null;
}

export interface MovieDetail {
  id: string;
  titleEn: string;
  titleVi: string;
  genre: string;
  releaseYear: number;
  durationMinutes?: number | null;
  descriptionEn?: string | null;
  descriptionVi?: string | null;
  directorEn?: string | null;
  directorVi?: string | null;
  castEn?: string | null;
  castVi?: string | null;
  directors?: MovieCredit[] | null;
  cast?: MovieCredit[] | null;
  posterUrl?: string | null;
  hasMedia?: boolean;
  isVip?: boolean;
  isFeatured?: boolean;
}

export interface SeriesSummary {
  id: string;
  titleEn: string;
  titleVi: string;
  genre: string;
  releaseYear: number;
  seasons: number;
  episodes: number;
  posterUrl?: string | null;
  hasMedia?: boolean;
  isVip?: boolean;
  isFeatured?: boolean;
  topCast?: string | null;
  topDirectors?: string | null;
}

export interface SeriesEpisode {
  id: string;
  seasonNumber: number;
  episodeNumber: number;
  title: string;
  durationMinutes?: number | null;
  hasMedia?: boolean;
  thumbnailUrl?: string | null;
  seasonYear?: number | null;
}

export interface SeriesDetail {
  id: string;
  titleEn: string;
  titleVi: string;
  genre: string;
  releaseYear: number;
  description?: string | null;
  descriptionEn?: string | null;
  descriptionVi?: string | null;
  directorEn?: string | null;
  directorVi?: string | null;
  castEn?: string | null;
  castVi?: string | null;
  directors?: MovieCredit[] | null;
  cast?: MovieCredit[] | null;
  posterUrl?: string | null;
  isVip?: boolean;
  episodes: SeriesEpisode[];
}

export interface SeriesSeasonGroup {
  seasonNumber: number;
  seasonYear?: number | null;
  episodes: SeriesEpisode[];
}

export interface CatalogQuery {
  q?: string;
  by?: 'title' | 'actor';
  genre?: string;
  country?: string;
  year?: number;
  featured?: boolean;
  sort?:
    | 'releaseYear'
    | 'created'
    | 'modified'
    | 'title'
    | 'views'
    | 'latestEpisode'
    | 'latestEpisodeRelease';
  page?: number;
  pageSize?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly authService: AuthService
  ) {}

  private async authHeaders(): Promise<HttpHeaders> {
    const token = await this.authService.getToken();
    return new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
  }

  private buildQuery(query: CatalogQuery): HttpParams {
    let params = new HttpParams();
    if (query.q) {
      params = params.set('q', query.q);
    }
    if (query.by) {
      params = params.set('by', query.by);
    }
    if (query.genre) {
      params = params.set('genre', query.genre);
    }
    if (query.country) {
      params = params.set('country', query.country);
    }
    if (query.year) {
      params = params.set('year', String(query.year));
    }
    if (query.featured) {
      params = params.set('featured', 'true');
    }
    if (query.sort) {
      params = params.set('sort', query.sort);
    }
    if (query.page) {
      params = params.set('page', String(query.page));
    }
    if (query.pageSize) {
      params = params.set('pageSize', String(query.pageSize));
    }
    return params;
  }

  async getMyProfile(): Promise<{
    externalUserId: string;
    displayName: string;
    language: string;
    timezone: string;
    email?: string | null;
    avatarUrl?: string | null;
    membershipLevel?: number;
    maxPersonalCollections?: number;
    createdAtUtc: string;
    updatedAtUtc: string;
  }> {
    const headers = await this.authHeaders();
    return firstValueFrom(
      this.httpClient
        .get<{
          externalUserId: string;
          displayName: string;
          language: string;
          timezone: string;
          email?: string | null;
          avatarUrl?: string | null;
          membershipLevel?: number;
          maxPersonalCollections?: number;
          createdAtUtc: string;
          updatedAtUtc: string;
        }>(`${environment.apiBaseUrl}/api/users/profile/me`, { headers })
        .pipe(timeout(25_000))
    );
  }

  async uploadMyAvatar(file: File): Promise<{
    avatarUrl?: string | null;
  }> {
    const headers = await this.authHeaders();
    const body = new FormData();
    body.append('file', file);
    return firstValueFrom(
      this.httpClient.post<{
        avatarUrl?: string | null;
      }>(`${environment.apiBaseUrl}/api/users/profile/me/avatar`, body, { headers })
    );
  }

  async getMovies(query: CatalogQuery = {}): Promise<PagedResult<MovieSummary> | MovieSummary[]> {
    const params = this.buildQuery(query);
    return firstValueFrom(
      this.httpClient.get<PagedResult<MovieSummary> | MovieSummary[]>(
        `${environment.apiBaseUrl}/api/movies`,
        { params }
      )
    );
  }

  async getMovie(idOrSlug: string): Promise<MovieDetail> {
    return firstValueFrom(
      this.httpClient.get<MovieDetail>(
        `${environment.apiBaseUrl}/api/movies/${encodeURIComponent(idOrSlug)}`
      )
    );
  }

  getMovieStreamUrl(idOrSlug: string): string {
    return `${environment.apiBaseUrl}/api/movies/${encodeURIComponent(idOrSlug)}/stream`;
  }

  async getSeries(query: CatalogQuery = {}): Promise<PagedResult<SeriesSummary> | SeriesSummary[]> {
    const params = this.buildQuery(query);
    return firstValueFrom(
      this.httpClient.get<PagedResult<SeriesSummary> | SeriesSummary[]>(
        `${environment.apiBaseUrl}/api/series`,
        { params }
      )
    );
  }

  async getSeriesDetail(idOrSlug: string): Promise<SeriesDetail> {
    const raw = await firstValueFrom(
      this.httpClient.get<unknown>(
        `${environment.apiBaseUrl}/api/series/${encodeURIComponent(idOrSlug)}`
      )
    );
    const detail = normalizeSeriesDetail(raw);
    if (!detail) {
      throw new Error('Invalid series detail response');
    }

    return detail;
  }

  getSeriesEpisodeStreamUrl(seriesIdOrSlug: string, episodeId: string): string {
    return `${environment.apiBaseUrl}/api/series/${encodeURIComponent(seriesIdOrSlug)}/episodes/${encodeURIComponent(episodeId)}/stream`;
  }

  async lookupMovies(
    ids: string[],
    options: { sort?: 'created' | 'releaseYear' | 'modified'; limit?: number } = {}
  ): Promise<MovieSummary[]> {
    if (ids.length === 0) {
      return [];
    }

    return firstValueFrom(
      this.httpClient.post<MovieSummary[]>(
        `${environment.apiBaseUrl}/api/movies/lookup`,
        {
          ids,
          sort: options.sort ?? 'created',
          limit: options.limit ?? 12
        }
      )
    );
  }

  async lookupSeries(
    ids: string[],
    options: { sort?: 'created' | 'releaseYear' | 'modified'; limit?: number } = {}
  ): Promise<SeriesSummary[]> {
    if (ids.length === 0) {
      return [];
    }

    return firstValueFrom(
      this.httpClient.post<SeriesSummary[]>(
        `${environment.apiBaseUrl}/api/series/lookup`,
        {
          ids,
          sort: options.sort ?? 'created',
          limit: options.limit ?? 12
        }
      )
    );
  }

  async recordMovieView(idOrSlug: string): Promise<void> {
    await firstValueFrom(
      this.httpClient
        .post(`${environment.apiBaseUrl}/api/movies/${encodeURIComponent(idOrSlug)}/views`, {})
        .pipe(timeout(5_000))
    );
  }

  async recordSeriesView(idOrSlug: string): Promise<void> {
    await firstValueFrom(
      this.httpClient
        .post(`${environment.apiBaseUrl}/api/series/${encodeURIComponent(idOrSlug)}/views`, {})
        .pipe(timeout(5_000))
    );
  }

  async getTalent(): Promise<unknown> {
    return firstValueFrom(this.httpClient.get(`${environment.apiBaseUrl}/api/talent`));
  }

  async getNews(): Promise<unknown> {
    return firstValueFrom(this.httpClient.get(`${environment.apiBaseUrl}/api/news`));
  }

  async getContinueWatching(): Promise<unknown> {
    const headers = await this.authHeaders();
    return firstValueFrom(
      this.httpClient
        .get(`${environment.apiBaseUrl}/api/library/continue-watching`, { headers })
        .pipe(timeout(5_000))
    );
  }

  async upsertContinueWatching(body: {
    contentType: string;
    contentId: string;
    episodeId?: string | null;
    watchedSeconds: number;
    durationSeconds: number;
  }): Promise<void> {
    const headers = await this.authHeaders();
    await firstValueFrom(
      this.httpClient
        .post(`${environment.apiBaseUrl}/api/library/continue-watching`, body, { headers })
        .pipe(timeout(5_000))
    );
  }

  async deleteContinueWatching(body: {
    contentType: string;
    contentId: string;
    episodeId?: string | null;
  }): Promise<void> {
    const headers = await this.authHeaders();
    await firstValueFrom(
      this.httpClient
        .post(`${environment.apiBaseUrl}/api/library/continue-watching/delete`, body, { headers })
        .pipe(timeout(5_000))
    );
  }

  async getWatchHistory(query?: { page?: number; pageSize?: number }): Promise<{
    items: Array<{
      contentType: string;
      contentId: string;
      episodeId?: string | null;
      watchedSeconds: number;
      durationSeconds: number;
      firstWatchedAtUtc: string;
      lastWatchedAtUtc: string;
    }>;
    total: number;
    page: number;
    pageSize: number;
  }> {
    const headers = await this.authHeaders();
    let params = new HttpParams();
    if (query?.page) {
      params = params.set('page', String(query.page));
    }
    if (query?.pageSize) {
      params = params.set('pageSize', String(query.pageSize));
    }
    return firstValueFrom(
      this.httpClient
        .get<{
          items: Array<{
            contentType: string;
            contentId: string;
            episodeId?: string | null;
            watchedSeconds: number;
            durationSeconds: number;
            firstWatchedAtUtc: string;
            lastWatchedAtUtc: string;
          }>;
          total: number;
          page: number;
          pageSize: number;
        }>(`${environment.apiBaseUrl}/api/library/watch-history`, { headers, params })
        .pipe(timeout(10_000))
    );
  }

  async recordWatchHistory(body: {
    contentType: string;
    contentId: string;
    episodeId?: string | null;
    watchedSeconds: number;
    durationSeconds: number;
  }): Promise<void> {
    const headers = await this.authHeaders();
    await firstValueFrom(
      this.httpClient
        .post(`${environment.apiBaseUrl}/api/library/watch-history`, body, { headers })
        .pipe(timeout(5_000))
    );
  }
}
